import React, { useEffect, useRef, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { LinearProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { CreateChancel, NotError, SelectTypeAutocomplete, TextRow } from 'components';
import {
    CONTENT_TYPE_ALLOWLIST_BY_PURPOSE,
    FindErrorItem,
    FindLoad,
    FindSuccessItem,
    Images,
    KeyPurpose,
    MAX_BYTES_BY_PURPOSE,
    renderSize,
    useModal,
} from 'utils';
import {
    httpRequestsOnErrorsActions,
    httpRequestsOnSuccessActions,
    tutorialVideosActions,
    uploadFile,
    uploadVideo,
} from 'store';
import { videoDefaultImages } from 'components/table/videoComponent/constants';
import {
    CATEGORY_OPTIONS,
    CREATE_ACTION_TYPE,
    EDIT_ACTION_TYPE,
    TutorialVideoCategory,
    readVideoDuration,
} from './constants';

const VIDEO_TYPES = ['MP4', 'MOV', 'WEBM'];
const IMAGE_TYPES = ['JPG', 'JPEG', 'PNG', 'WEBP'];

export const TutorialVideoForm = ({ video, afterSuccess }) => {
    const dispatch = useDispatch();
    const { close } = useModal();
    const isEdit = !!video;
    const actionType = isEdit ? EDIT_ACTION_TYPE : CREATE_ACTION_TYPE;
    const editLoader = !!FindLoad(actionType)?.length;
    const editSuccess = FindSuccessItem(actionType);
    const editError = FindErrorItem(actionType);
    const [videoFile, setVideoFile] = useState(null);
    const currentCoverKey = video?.thumbnail?.key;
    const currentIsStock = videoDefaultImages.some((image) => image.key === currentCoverKey);
    const [coverImage, setCoverImage] = useState(isEdit ? null : videoDefaultImages[0]);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [title, setTitle] = useState(video?.title || '');
    const [description, setDescription] = useState(video?.description || '');
    const [category, setCategory] = useState(video?.category || TutorialVideoCategory.GENERAL);
    const [uploadLoader, setUploadLoader] = useState(false);
    const [uploadPercent, setUploadPercent] = useState(null);
    const [error, setError] = useState('');
    const coverInputRef = useRef(null);
    const blobUrlsRef = useRef([]);

    useEffect(() => () => blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url)), []);

    // The modal closes only once the api has accepted the save — not on
    // dispatch — so a rejected request leaves the form open and filled in.
    useEffect(() => {
        if (!editSuccess) return;
        dispatch(httpRequestsOnSuccessActions.removeSuccess(actionType));
        toast(<NotError errMessage={isEdit ? 'Video updated successfully!' : 'Video added successfully!'} />);
        close();
        afterSuccess?.();
    }, [editSuccess]);

    useEffect(() => {
        if (!editError) return;
        const message = Array.isArray(editError.error) ? editError.error[0] : editError.error;
        failToast(typeof message === 'string' && message ? message : 'Something went wrong. Please try again.');
        dispatch(httpRequestsOnErrorsActions.removeError(actionType));
    }, [editError]);

    useEffect(() => () => dispatch(httpRequestsOnErrorsActions.removeError(actionType)), []);

    const failToast = (message) => toast(<NotError type={'error'} errMessage={message} />);

    const handleUploadVideo = (file) => {
        if (!file) return;
        // Both limits are enforced again by the presign endpoint; checking
        // here just avoids pushing gigabytes before finding out.
        if (file.size > MAX_BYTES_BY_PURPOSE[KeyPurpose.TUTORIAL_VIDEO]) {
            failToast('Video must be smaller than 5 GB.');
            return;
        }
        if (!CONTENT_TYPE_ALLOWLIST_BY_PURPOSE[KeyPurpose.TUTORIAL_VIDEO].includes(file.type)) {
            failToast('Video must be an MP4, WebM or MOV file.');
            return;
        }
        setError('');
        setVideoFile(file);
        // Prefill the title from the filename the first time, the way a
        // person would type it anyway. Never overwrites something typed.
        if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''));
    };

    const handleUploadCover = (file) => {
        if (!file) return;
        if (file.size > MAX_BYTES_BY_PURPOSE[KeyPurpose.TUTORIAL_THUMBNAIL]) {
            failToast('Cover image must be smaller than 5 MB.');
            return;
        }
        if (!CONTENT_TYPE_ALLOWLIST_BY_PURPOSE[KeyPurpose.TUTORIAL_THUMBNAIL].includes(file.type)) {
            failToast('Cover image must be a PNG, JPEG or WebP file.');
            return;
        }
        const image = { file, url: URL.createObjectURL(file) };
        blobUrlsRef.current.push(image.url);
        setUploadedImages((prev) => [image, ...prev]);
        setCoverImage(image);
    };

    const removeUploadedImage = (image) => {
        setUploadedImages((prev) => prev.filter((item) => item !== image));
        if (coverImage === image) setCoverImage(null);
    };

    const isStockSelected = (image) =>
        coverImage ? !coverImage.file && coverImage.key === image.key : currentCoverKey === image.key;

    const selectedStyle = (isSelected) => (isSelected ? { border: '3px solid #0C66E4', cursor: 'pointer' } : { cursor: 'pointer' });

    const handleSubmit = async () => {
        if (!title.trim()) {
            setError('title');
            return;
        }
        if (!isEdit && !videoFile) {
            setError('file');
            failToast('Please choose a video file.');
            return;
        }

        setUploadLoader(true);
        try {
            const body = { title: title.trim(), description: description || '', category };

            if (videoFile) {
                // Nothing server-side ever opens the file — bytes go straight
                // to S3 — so the runtime has to be read here or not at all.
                body.durationSec = await readVideoDuration(videoFile);
                setUploadPercent(0);
                body.file = await uploadVideo(videoFile, KeyPurpose.TUTORIAL_VIDEO, {
                    onProgress: ({ percent }) => setUploadPercent(percent),
                });
            }
            if (coverImage?.file) {
                body.thumbnail = await uploadFile(coverImage.file, KeyPurpose.TUTORIAL_THUMBNAIL);
            } else if (coverImage?.key && coverImage.key !== currentCoverKey) {
                body.thumbnail = { key: coverImage.key, originalName: coverImage.originalName };
            }

            if (isEdit) {
                dispatch(tutorialVideosActions.editTutorialVideo(video.id || video._id, body));
            } else {
                dispatch(tutorialVideosActions.createTutorialVideo(body));
            }
        } catch (err) {
            // The multipart upload aborts itself on failure, and no record was
            // created, so there is nothing to roll back — just say so.
            const message =
                err?.data?.message?.[0] || err?.data?.message || err?.message || 'Upload failed. Please try again.';
            failToast(message);
        } finally {
            setUploadLoader(false);
            setUploadPercent(null);
        }
    };

    return (
        <div style={{ width: 400 }}>
            <div>
                <p className="modal-header-title">{isEdit ? 'Edit Tutorial Video' : 'Upload Tutorial Video'}</p>
                <p className="select-document-desc">
                    {isEdit
                        ? 'Update the details, or replace the video and its cover image.'
                        : 'Upload a video that shows your team how something is done in TherapyLake.'}
                </p>
            </div>

            <div>
                {!videoFile && (
                    <div style={{ border: 'none' }} className="upload-file-header-box">
                        <FileUploader handleChange={handleUploadVideo} name="file" types={VIDEO_TYPES}>
                            <div className="drag-drop-component" style={error === 'file' ? { borderColor: '#FE7070' } : {}}>
                                <div className="upload-dock-icon">
                                    <img src={Images.uploadDockCloud} alt="upload" style={{ margin: 0 }} />
                                </div>
                                <label className="custom-file-upload">
                                    <input
                                        onClick={(event) => (event.target.value = null)}
                                        onChange={(event) => handleUploadVideo(event.target.files?.[0])}
                                        type="file"
                                        accept="video/*"
                                        id="tutorial-video-file"
                                    />
                                    <i className="fa fa-cloud-upload" />
                                    <div className="upload">
                                        <p>
                                            <span style={{ fontWeight: 600 }}>Drag & drop</span>
                                            <span style={{ margin: '0 4px' }}>your video here or</span>
                                            <span style={{ color: '#2E6DD8', fontWeight: 600 }}>click to upload</span>
                                        </p>
                                    </div>
                                </label>
                            </div>
                        </FileUploader>

                        {isEdit && (
                            <p className="select-document-desc" style={{ marginTop: 8 }}>
                                Leave this empty to keep the current video.
                            </p>
                        )}
                    </div>
                )}

                <div className="uploaded-files-wrapper" style={{ maxHeight: 450 }}>
                    <div
                        className="loaded-documents-box"
                        style={{
                            gap: 0,
                            margin: '0 0 8px 0',
                            flexDirection: 'column',
                            borderColor: error === 'title' ? '#FE7070' : '#E4E7EC',
                        }}
                    >
                        {videoFile && (
                            <div className="uploaded-file-info-header">
                                <div className="space-between">
                                    <div className="flex-align-center" style={{ gap: 8, width: '90%' }}>
                                        <img src={Images.uploadedVideo} alt="icon" />
                                        <p className="loaded-document-name full-width">
                                            <TextRow name={videoFile?.name} fontWeight={600} textWidth={10} />
                                        </p>
                                    </div>
                                    <button className="reset-btn-by-default" onClick={() => setVideoFile(null)} disabled={uploadLoader}>
                                        <img src={Images.redTrashLine} alt="icon" />
                                    </button>
                                </div>
                                <div className="uploaded-file-detail-box" style={{ marginLeft: 32 }}>
                                    <p>{renderSize(videoFile)}</p>
                                    <div className="loaded-circle" />
                                    {videoFile?.type && <p>{videoFile.type.replace('video/', '')}</p>}
                                </div>
                                {uploadPercent !== null && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 0 32px' }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={uploadPercent}
                                            sx={{ flex: 1, height: 6, borderRadius: 3 }}
                                        />
                                        <p style={{ fontSize: 12, fontWeight: 600, minWidth: 36, textAlign: 'right' }}>{uploadPercent}%</p>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="full-width">
                            <div className="choose-or-upload-image">
                                <p>Choose or upload an image to use as the video cover</p>
                            </div>
                            <div className="choose-or-upload-image-actions">
                                <FileUploader handleChange={handleUploadCover} name="file" types={IMAGE_TYPES}>
                                    <div className="drag-drop-video-background-image">
                                        <label className="custom-file-upload">
                                            <input
                                                ref={coverInputRef}
                                                onClick={(event) => (event.target.value = null)}
                                                onChange={(event) => handleUploadCover(event.target.files?.[0])}
                                                type="file"
                                                accept="image/*"
                                                id="tutorial-thumbnail-file"
                                            />
                                            <i className="fa fa-cloud-upload" />
                                            <div className="upload">
                                                <p>
                                                    <span style={{ color: '#2E6DD8', fontWeight: 600 }}>
                                                        Upload Image, Drag & drop
                                                    </span>
                                                    <span style={{ margin: '0 4px' }}>or Select from Frames</span>
                                                    <span>JPG, PNG, WebP (Max size: 5 MB)</span>
                                                </p>
                                            </div>
                                        </label>
                                    </div>
                                </FileUploader>

                                <div className="video-default-images-wrapper">
                                    {videoDefaultImages.map((image) => (
                                        <div
                                            key={image.key}
                                            onClick={() => setCoverImage(image)}
                                            style={selectedStyle(isStockSelected(image))}
                                            className="video-default-image"
                                        >
                                            <img src={image.url} alt="cover" />
                                        </div>
                                    ))}

                                    {video?.thumbnail?.url && !currentIsStock && (
                                        <div
                                            title="Current cover"
                                            onClick={() => setCoverImage(null)}
                                            style={selectedStyle(!coverImage)}
                                            className="video-default-image"
                                        >
                                            <img src={video.thumbnail.url} alt="current cover" />
                                        </div>
                                    )}

                                    {uploadedImages.map((image) => (
                                        <div key={image.url} style={{ position: 'relative' }}>
                                            <button className="remove-uploaded-image" onClick={() => removeUploadedImage(image)}>
                                                <img src={Images.remove} alt="removeImage" />
                                            </button>
                                            <div
                                                onClick={() => setCoverImage(image)}
                                                style={selectedStyle(coverImage === image)}
                                                className="video-default-image"
                                            >
                                                <img src={image.url} alt="preview" />
                                            </div>
                                        </div>
                                    ))}

                                    <div>
                                        <button
                                            type="button"
                                            className="add-back-image-btn"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                coverInputRef.current?.click();
                                            }}
                                        >
                                            <img src={Images.plus} alt="add" />
                                        </button>
                                    </div>
                                </div>

                            </div>

                            <div className="uploaded-file-name-input-wrapper">
                                <p>Title*</p>
                                <input
                                    onChange={(e) => {
                                        if (error === 'title') setError('');
                                        setTitle(e.target.value);
                                    }}
                                    value={title}
                                    type="text"
                                    name="title"
                                    placeholder="Title*"
                                    maxLength={120}
                                />
                            </div>

                            <div className="uploaded-file-desc-input-wrapper">
                                <textarea
                                    onChange={(e) => setDescription(e.target.value)}
                                    value={description}
                                    name="description"
                                    placeholder="Description"
                                    maxLength={500}
                                />
                            </div>

                            <div style={{ marginTop: 8 }}>
                                <SelectTypeAutocomplete
                                    uiType={'tableFilter'}
                                    name="category"
                                    title="Section"
                                    placeholder="Section"
                                    list={CATEGORY_OPTIONS}
                                    defaultValue={category}
                                    handleSelect={(ev) => setCategory(ev?.target?.value)}
                                    renderValue={(i) => i?.name}
                                    disableClearable
                                    noError
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <CreateChancel
                loader={uploadLoader || editLoader}
                create={isEdit ? 'Save Changes' : 'Upload Video'}
                chancel={'Cancel'}
                onCreate={handleSubmit}
                onClose={close}
                buttonWidth="223px"
                createButnMargin={'16px'}
                disabled={(!isEdit && !videoFile) || uploadLoader || editLoader}
            />
        </div>
    );
};
